'use client';
import { UiKitButton } from '@components/UiKitButton';
import { UiKitModal } from '@components/UiKitModal';
import type { TpAccount } from '@features/Account/types';
import { use$ } from '@legendapp/state/react';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { customFetchClientSide } from 'src/api/customFetchClientSide';
import { INNER_API_ROUTES } from 'src/constants';
import { useRootStore } from 'src/providers';
import { useRestoreSubscription } from 'src/utils/hooks/useRestoreSubscription';
import styles from './InactiveSubscriptionAlert.module.css';

type ModalState = 'idle' | 'success' | 'error';

async function getAppFromHostname() {
  if (typeof window === 'undefined') {
    return '';
  }

  const appFromCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('x-app'))
    ?.split('=')[1];
  if (appFromCookie) {
    return appFromCookie;
  }

  return window.location.hostname.split('.')[0];
}

export const InactiveSubscriptionAlert = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalState, setModalState] = useState<ModalState>('idle');
  const store$ = useRootStore();
  const restoreSubscription = useRestoreSubscription();

  const path = usePathname();
  useEffect(() => {
    if (path) {
      store$.user.wasInactiveSubscriptionClosed.set(false);
    }
  }, [path, store$.user.wasInactiveSubscriptionClosed]);

  const {
    wasInactiveSubscriptionClosed,
    setWasInactiveSubscriptionClosed,
    email,
    userId,
  } = use$(() => ({
    wasInactiveSubscriptionClosed:
      store$.user.wasInactiveSubscriptionClosed.get(),
    setWasInactiveSubscriptionClosed: () =>
      store$.user.wasInactiveSubscriptionClosed.set(true),
    email: store$.user.email.get(),
    userId: store$.user.userId.get(),
  }));

  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: (): Promise<TpAccount> =>
      customFetchClientSide(INNER_API_ROUTES.PROFILE),
    staleTime: 30 * 1000, // 30 sec
    gcTime: 30 * 1000,
  });

  useEffect(() => {
    if (
      profile.data &&
      !profile.data.subscription_active &&
      !wasInactiveSubscriptionClosed
    ) {
      setIsOpen(true);
    }
  }, [profile, wasInactiveSubscriptionClosed]);

  const handleRestoreClick = async () => {
    const app = await getAppFromHostname();

    restoreSubscription.mutate(
      {
        app,
        user_id: userId,
        email,
      },
      {
        onSuccess: () => {
          setModalState('success');
        },
        onError: () => {
          setModalState('error');
        },
      },
    );
  };

  console.log(modalState);

  const handleClose = () => {
    if (!restoreSubscription.isPending) {
      setWasInactiveSubscriptionClosed();
      setIsOpen(false);
      setModalState('idle');
    }
  };

  const handleContinue = () => {
    setIsOpen(false);
    setWasInactiveSubscriptionClosed();
    setModalState('idle');
  };

  const renderContent = () => {
    switch (modalState) {
      case 'success':
        return (
          <>
            <h1 className={styles.modalTitle}>You're all set</h1>
            <p className={styles.subTitle}>
              Your access has been successfully restored
            </p>
            <div className={styles.modalButtons}>
              <UiKitButton
                className={styles.modalButton}
                color="primary"
                onClick={handleContinue}
              >
                Continue
              </UiKitButton>
            </div>
          </>
        );

      case 'error':
        return (
          <>
            <h1 className={styles.modalTitle}>Something went wrong</h1>
            <p className={styles.subTitle}>
              Check your internet connection and try again
            </p>
            <div className={styles.modalButtons}>
              <UiKitButton
                className={styles.modalButton}
                color="primary"
                onClick={handleRestoreClick}
                disabled={restoreSubscription.isPending}
              >
                {restoreSubscription.isPending ? 'Loading...' : 'Try again'}
              </UiKitButton>
              <UiKitButton
                className={styles.modalButton}
                color="secondary"
                onClick={() => {
                  // Contact Us logic
                  window.location.href = 'mailto:support@mentalgrowth.app';
                }}
              >
                Contact Us
              </UiKitButton>
            </div>
          </>
        );
      default:
        return (
          <>
            <h1 className={styles.modalTitle}>
              Your subscription is no longer active
            </h1>
            <p className={styles.subTitle}>
              Renew your subscription to regain access
            </p>
            <div className={styles.modalButtons}>
              <UiKitButton
                className={styles.modalButton}
                color="primary"
                onClick={handleRestoreClick}
                disabled={restoreSubscription.isPending}
              >
                {restoreSubscription.isPending
                  ? 'Loading...'
                  : 'Restore subscription'}
              </UiKitButton>
            </div>
          </>
        );
    }
  };

  return (
    <UiKitModal
      show={isOpen}
      onClose={handleClose}
      contentClassName={styles.modalContent}
      // disableBackdrop={restoreSubscription.isPending}
    >
      {renderContent()}
    </UiKitModal>
  );
};
